/**
 * ProtectedRoute.jsx — DeutschBridge Institute
 *
 * FIX: Students with no completed payment are redirected to
 * /payment/pending instead of being allowed into the dashboard.
 *
 * Gate order:
 *   1. Not logged in            → /login
 *   2. Wrong role               → / (root redirect handles it)
 *   3. Student, payment check
 *      still loading            → spinner (brief, avoids flicker)
 *   4. Student, not paid, not
 *      on an allowed unpaid URL → /payment/pending
 *   5. All clear                → render children
 */
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

// Spinner shown while we wait for the payment check API call
function PaymentCheckSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <svg className="animate-spin w-10 h-10 mx-auto mb-3" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="#DD0000" strokeWidth="3"
            strokeDasharray="50 10" strokeLinecap="round" />
        </svg>
        <p className="text-sm text-gray-500">Verifying your account…</p>
      </div>
    </div>
  );
}

export function ProtectedRoute({ children, requiredRole }) {
  const { user, loading, hasPaid, paymentChecking, isUnpaidAllowed } = useAuth();
  const location = useLocation();

  // 1. Auth loading (rehydrating from localStorage)
  if (loading) return <PaymentCheckSpinner />;

  // 2. Not logged in
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 3. Role check
  if (requiredRole) {
    const ROLE_HIERARCHY = {
      student:     ['student'],
      teacher:     ['teacher', 'admin', 'super_admin'],
      admin:       ['admin', 'super_admin'],
      super_admin: ['super_admin'],
    };
    const allowed = ROLE_HIERARCHY[requiredRole] || [requiredRole];
    if (!allowed.includes(user.role)) {
      return <Navigate to="/" replace />;
    }
  }

  // 4. Payment gate — students only
  if (user.role === 'student') {
    // Still waiting for the payment API response
    if (hasPaid === null || paymentChecking) {
      return <PaymentCheckSpinner />;
    }

    // Not paid and not on an allowed unpaid route
    if (!hasPaid && !isUnpaidAllowed(location.pathname)) {
      return <Navigate to="/payment/pending" replace />;
    }
  }

  // 5. All clear
  return children;
}
